// Lightweight inline markup for instrument text (Church Health Assessment).
// Same convention as the corpus note markup (deliberately copied, not
// cross-imported — parishes must not depend on @cca/corpus-shared): a plain
// string marked up with a tiny markdown subset:
//   **bold**   *italic*   _underline_
// Non-nesting, inline-only, one mark per token. The web renders tokens to
// <strong>/<em>/<u> React text nodes (no innerHTML), the worker strips to
// plain text for AI prompts and re-escapes token text for the debrief PDF.

export interface InlineMarkupToken {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
}

// `**` (bold) is tried before `*` (italic); inner content can't contain its own
// delimiter, so `**x**` matches the bold branch, never italic.
const PATTERN = /\*\*([^*]+)\*\*|\*([^*]+)\*|_([^_]+)_/g;

/** Split marked-up text into styled tokens. Plain text → a single unstyled token. */
export function tokenizeInlineMarkup(body: string): InlineMarkupToken[] {
  const out: InlineMarkupToken[] = [];
  const re = new RegExp(PATTERN); // fresh instance — PATTERN carries /g state
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    if (m.index > last) out.push({ text: body.slice(last, m.index) });
    if (m[1] !== undefined) out.push({ text: m[1], bold: true });
    else if (m[2] !== undefined) out.push({ text: m[2], italic: true });
    else if (m[3] !== undefined) out.push({ text: m[3], underline: true });
    last = re.lastIndex;
  }
  if (last < body.length) out.push({ text: body.slice(last) });
  return out;
}

/** The text with all markup delimiters removed (for plain-text consumers). */
export function stripInlineMarkup(body: string): string {
  return tokenizeInlineMarkup(body)
    .map((t) => t.text)
    .join('');
}

/** Serialise tokens back to the string form. One mark per token, priority
 *  bold > italic > underline (the tokenizer is non-nesting). Empty tokens are
 *  dropped; delimiter characters inside a styled token would break the
 *  round-trip, so they are removed from styled text. */
export function tokensToMarkup(tokens: InlineMarkupToken[]): string {
  let out = '';
  for (const t of tokens) {
    if (!t.text) continue;
    if (t.bold) out += `**${t.text.replace(/\*/g, '')}**`;
    else if (t.italic) out += `*${t.text.replace(/\*/g, '')}*`;
    else if (t.underline) out += `_${t.text.replace(/_/g, '')}_`;
    else out += t.text;
  }
  return out;
}
